import type * as THREE from 'three'
import { extremosTampaTraseira, type PerfilVeiculo } from '../../dominio/didatico/perfilVeiculo'
import { vetor, type Vetor3 } from '../../dominio/geometria/vetor3'
import { orientacaoEntre, pontoMedio } from '../componentes/Barra'
import { Y_BASE_FASCIA } from './geometriaDeformacaoFrontal'
import { criarCaixaArredondada, geometriaBase, type TransformacaoRepouso } from './malhaDeformavel'

export { extremosTampaTraseira }

/**
 * Traseira deformável.
 *
 * O campo da traseira (`deslocamentoLongitudinal` com sentido −1) não é a
 * frente espelhada: o para-choque SOBE (quem bate mergulha por baixo), a tampa
 * entra pela borda de baixo e dobra em dois vincos, as lanternas se deslocam e
 * o piso do porta-malas levanta no severo. Aqui fica a base da tampa.
 */

export function baseTampaTraseira(perfil: PerfilVeiculo, largura: number, espessura: number): THREE.BufferGeometry {
  const { de, para } = extremosTampaTraseira(perfil)
  const altura = Math.hypot(para.y - de.y, para.z - de.z)
  return geometriaBase(`tampaTraseira:${perfil.id}:${largura.toFixed(3)}`, () =>
    criarCaixaArredondada(largura, altura, espessura, [16, 12, 1], 0.025),
  )
}

/** Pose do painel inclinado entre `de` e `para` (+Y local ao longo do painel). */
export function repousoPainelInclinado(de: Vetor3, para: Vetor3): TransformacaoRepouso {
  const centro = pontoMedio(de, para)
  return { posicao: [centro.x, centro.y, centro.z], quaternion: orientacaoEntre(de, para) }
}

/**
 * Topo do para-choque traseiro do sedan. Ele ia até a linha de cintura e virava
 * uma parede escura de ponta a ponta; na altura de um para-choque, a rabeta do
 * porta-malas aparece acima dele.
 */
export const Y_TOPO_PARA_CHOQUE_TRASEIRO_SEDAN = Y_BASE_FASCIA + 0.25

/**
 * Saia da tampa do sedan: a face de trás do porta-malas, que desce do tampo até
 * o para-choque e levanta junto com ele. Sem ela o porta-malas era só um tampo
 * horizontal e a traseira, um muro.
 */
export function extremosSaiaTampaSedan(perfil: PerfilVeiculo): { de: Vetor3; para: Vetor3 } {
  const { para } = extremosTampaTraseira(perfil)
  return {
    // começa rente ao tampo, para as duas peças formarem um L sem fresta
    de: vetor(0, para.y + 0.025, para.z),
    para: vetor(0, Y_TOPO_PARA_CHOQUE_TRASEIRO_SEDAN - 0.02, para.z),
  }
}

export function baseSaiaTampaSedan(perfil: PerfilVeiculo, largura: number, espessura: number): THREE.BufferGeometry {
  const { de, para } = extremosSaiaTampaSedan(perfil)
  const altura = Math.hypot(para.y - de.y, para.z - de.z)
  return geometriaBase(`saiaTampa:${perfil.id}:${largura.toFixed(3)}`, () =>
    criarCaixaArredondada(largura, altura, espessura, [16, 6, 1], 0.025),
  )
}
